import React, { useState, useRef, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import useV3OfferEditController from '../../hooks/useV3OfferEditController';
import { V3_ACTIONS } from '../../contexts/V3OfferContext';
import SimpleDealHeader from './SimpleDealHeader';
import V3OfferEditModals from '../V3OfferEditModals';
import SimpleOfferTabNav from './SimpleOfferTabNav';
import SimpleDealsTab from './SimpleDealsTab';
import SimpleRevenueTab from './SimpleRevenueTab';
import SimpleExpensesTab from './SimpleExpensesTab';
import SimpleContactsTab from './SimpleContactsTab';
import SimpleNotesTab from './SimpleNotesTab';
import SimpleOfferRail from './SimpleOfferRail';
import SettingsChangelog from '../../../../shared/SettingsChangelog';
import useTheme from '../../../shared/hooks/useTheme';
import '../../styles/simpleOfferLayout.css';

const SECTION_PREFIXES = ['tickets','other_income','before_adjusted_deductions','additions','ancillary_income','expenses','deductions','ancillary_expenses'];

const SimpleOfferLayout = () => {
  const ctrl = useV3OfferEditController();
  const [active, setActive] = useState('deals');
  const [railCollapsed, setRailCollapsed] = useState(false);
  const columnDefaultsApplied = useRef(false);

  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (columnDefaultsApplied.current || !ctrl.budget?.id) return;
    columnDefaultsApplied.current = true;
    const flags = {};
    SECTION_PREFIXES.forEach((p) => {
      flags[`${p}_show_forecast`] = false;
      flags[`${p}_show_actuals`] = false;
    });
    ctrl.dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: flags });
  }, [ctrl.budget?.id, ctrl.dispatch]);

  if (!ctrl.offer || !ctrl.budget) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin mr-2" />
        Loading...
      </div>
    );
  }

  const contactsCount = (ctrl.offerContacts || []).filter((c) => c.id !== undefined).length;
  const notesCount = ctrl.offer?.additional_terms && String(ctrl.offer.additional_terms).trim() ? 1 : 0;
  const dealsCount = (ctrl.offer?.lineup || []).length || 1;

  const tabs = [
    { key: 'deals', label: 'Deals', count: dealsCount },
    { key: 'revenue', label: 'Revenue' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'contacts', label: 'Contacts', count: contactsCount },
    { key: 'notes', label: 'Additional Terms', count: notesCount },
  ];

  return (
    <div className="simple-offer-page" data-theme={resolvedTheme}>
      <ToastContainer icon={false} />
      <SimpleDealHeader
        ctrl={ctrl}
        tabNav={<SimpleOfferTabNav tabs={tabs} active={active} onSelect={setActive} />}
        changelog={<SettingsChangelog lastEditedAt={ctrl.lastEditedAt} changelogVersions={ctrl.changelogVersions} />}
      />

      <div className={`simple-offer-body${railCollapsed ? ' simple-offer-body--rail-collapsed' : ''}`}>
        <div
          className="simple-offer-body__main"
          role="tabpanel"
          id="simple-offer-tabpanel"
          aria-labelledby={`simple-offer-tab-${active}`}
        >
          {active === 'deals' && <SimpleDealsTab ctrl={ctrl} />}
          {active === 'revenue' && <SimpleRevenueTab ctrl={ctrl} />}
          {active === 'expenses' && <SimpleExpensesTab ctrl={ctrl} />}
          {active === 'contacts' && <SimpleContactsTab ctrl={ctrl} />}
          {active === 'notes' && <SimpleNotesTab ctrl={ctrl} />}
        </div>
        <SimpleOfferRail ctrl={ctrl} collapsed={railCollapsed} onToggle={() => setRailCollapsed((c) => !c)} />
      </div>

      <V3OfferEditModals ctrl={ctrl} />
    </div>
  );
};

export default SimpleOfferLayout;
